import { Link } from 'react-router-dom';
import { useTenantList } from '../../api/tenants';
import { useUsers } from '../../api/users';
import { EmptyState, ErrorState, SkeletonStats, SkeletonTable } from '../../components/states';
import { DataTable } from '../../components/table/DataTable';
import {
  Badge,
  PageHeader,
  Section,
  Stat,
  formatDate,
  statusLabel,
  statusTone,
} from '../../components/ui';
import { formatNumber } from '../../format';

const RECENT = 5;

/**
 * Staff landing page: how many tenants, users and staff accounts the hub has,
 * and the newest tenants. Everything else lives on its own page.
 */
export function StaffDashboard() {
  const tenants = useTenantList({ limit: RECENT });
  const users = useUsers({ limit: 1 });
  const staff = useUsers({ userType: 'staff', limit: 1 });

  const counts = [
    { label: 'Tenants', query: tenants },
    { label: 'Users', query: users },
    { label: 'Staff', query: staff },
  ];
  const countsPending = counts.some((c) => c.query.isPending);
  const page = tenants.data;

  return (
    <>
      <PageHeader title="Overview" />

      {countsPending ? (
        <SkeletonStats count={counts.length} />
      ) : (
        <div className="stats">
          {counts.map((c) => (
            <Stat
              key={c.label}
              label={c.label}
              value={c.query.data ? formatNumber(c.query.data.pagination.total) : '—'}
              reference={c.query.isError ? 'could not load' : undefined}
            />
          ))}
        </div>
      )}

      <Section title="Newest tenants" actions={<Link to="/staff/tenants">All tenants</Link>}>
        {tenants.isError ? (
          <ErrorState
            error={tenants.error}
            onRetry={() => tenants.refetch()}
            retrying={tenants.isFetching}
          />
        ) : null}
        {tenants.isPending ? <SkeletonTable rows={3} cols={4} /> : null}
        {page && page.data.length === 0 ? (
          <EmptyState
            title="No tenants yet"
            body="Tenants appear once the first organization is onboarded."
          />
        ) : null}
        {page && page.data.length > 0 ? (
          <DataTable
            label="Newest tenants"
            columns={[
              { key: 'name', header: 'Name' },
              { key: 'status', header: 'Status' },
              { key: 'manager', header: 'Account manager' },
              { key: 'created', header: 'Created', align: 'num' },
            ]}
          >
            {page.data.map((t) => (
              <tr key={t.id}>
                <td>
                  <Link to={`/tenant/${t.id}`} data-primary>
                    {t.name}
                  </Link>{' '}
                  <code>{t.slug}</code>
                </td>
                <td>
                  <Badge tone={statusTone(t.status)}>{statusLabel(t.status)}</Badge>
                </td>
                <td>
                  {t.accountManager ? (
                    t.accountManager.email
                  ) : (
                    <span className="muted">Unassigned</span>
                  )}
                </td>
                <td className="num">
                  <time dateTime={t.createdAt}>{formatDate(t.createdAt)}</time>
                </td>
              </tr>
            ))}
          </DataTable>
        ) : null}
      </Section>
    </>
  );
}
